import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SessionAuth, useSessionContext } from 'supertokens-auth-react/recipe/session';
import { api } from '../apiClient';

const AdminUserCreditsContent = () => {
  const session = useSessionContext();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [currentCredits, setCurrentCredits] = useState(null);
  const [creditsDelta, setCreditsDelta] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const roles = session.accessTokenPayload && session.accessTokenPayload['st-role']?.v;
  const isAdmin = roles && roles.includes('admin');

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin]);

  useEffect(() => {
    if (!selectedUserId) {
      setCurrentCredits(null);
      return;
    }
    fetchUserCredits(selectedUserId);
  }, [selectedUserId]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (err) {
      setError('Failed to fetch users.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCredits = async (userId) => {
    setLoadingCredits(true);
    setError(null);
    try {
      const response = await api.get(`/admin/users/${encodeURIComponent(userId)}/credits`);
      setCurrentCredits(response.data.credits);
    } catch (err) {
      setCurrentCredits(null);
      setError(err.response?.data?.detail || 'Failed to fetch credits for user.');
    } finally {
      setLoadingCredits(false);
    }
  };

  const handleAdjustCredits = async (e) => {
    e.preventDefault();
    const delta = parseInt(creditsDelta, 10);
    if (isNaN(delta) || delta === 0) {
      setError('Enter a non-zero number (positive to add, negative to subtract).');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.patch(
        `/admin/users/${encodeURIComponent(selectedUserId)}/credits`,
        { credits_delta: delta }
      );
      setCurrentCredits(response.data.credits);
      setCreditsDelta('');
      setMessage(
        delta > 0
          ? `Added ${delta} credits. New balance: ${response.data.credits}`
          : `Subtracted ${-delta} credits. New balance: ${response.data.credits}`
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update credits.');
    } finally {
      setLoading(false);
    }
  };

  if (session.loading) return null;

  if (!isAdmin) {
    return (
      <div className="container mx-auto p-4 text-center mt-10">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="text-gray-600 mt-2">You do not have permission to view this page.</p>
        <Link to="/" className="text-blue-500 hover:underline mt-4 block">Go Home</Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4">
        <Link to="/admin" className="text-blue-600 hover:underline">← Back to Admin</Link>
      </div>
      <h1 className="text-3xl font-bold mb-2 text-center">User Credits (Image Generation)</h1>
      <p className="text-gray-600 text-center mb-6">
        Add or subtract credits (tokens) for image generation for a specific user.
      </p>

      {error && <p className="text-red-500 text-center mb-4">{error}</p>}
      {message && <p className="text-green-500 text-center mb-4">{message}</p>}

      <div className="max-w-xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <form onSubmit={handleAdjustCredits}>
          <div className="mb-4">
            <label htmlFor="user" className="block text-gray-700 font-bold mb-2">Select User</label>
            <select
              id="user"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              required
            >
              <option value="">Select a user</option>
              {users.map((user) => (
                <option key={user.user_id} value={user.user_id}>
                  {user.full_name ? `${user.full_name} (${user.email})` : user.email}
                </option>
              ))}
            </select>
          </div>

          {selectedUserId && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700 font-medium">Current credits: </span>
              {loadingCredits ? (
                <span className="text-gray-500">Loading…</span>
              ) : (
                <span className="font-bold text-lg">{currentCredits ?? '—'}</span>
              )}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="creditsDelta" className="block text-gray-700 font-bold mb-2">
              Credits to add (or subtract)
            </label>
            <input
              type="number"
              id="creditsDelta"
              value={creditsDelta}
              onChange={(e) => setCreditsDelta(e.target.value)}
              placeholder="e.g. 10 or -5"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              required
            />
            <p className="mt-1 text-sm text-gray-500">
              Use a positive number to add credits, negative to subtract.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading || !selectedUserId}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Updating…' : 'Update credits'}
          </button>
        </form>
      </div>
    </div>
  );
};

const AdminUserCredits = () => (
  <SessionAuth>
    <AdminUserCreditsContent />
  </SessionAuth>
);

export default AdminUserCredits;
